import { useEffect, useRef, useState } from 'react';
import { Card, Table, Button, Spinner, Alert, Badge, Form, Row, Col } from 'react-bootstrap';
import type { components } from '@telemedicine/shared';
import {
  listCaseDocuments,
  uploadCaseDocument,
  getCaseDocumentDownloadUrl,
} from '../services/api';
import { personName } from '../people';
import { enumLabel } from '../labels';
import { getLang } from '../language';
import { BusyButton } from '../components/BusyButton';

type Document = components['schemas']['Document'];
type DocumentType = components['schemas']['DocumentType'];

const DOCUMENT_TYPES: DocumentType[] = [
  'BLOOD_RESULT',
  'SCAN',
  'XRAY',
  'MRI',
  'CT',
  'MEDICAL_REPORT',
  'PHOTOGRAPH',
  'OTHER',
];

const ACCEPT = 'application/pdf,image/jpeg,image/png';
const MAX_BYTES = 50 * 1024 * 1024;

const scanVariant: Record<string, string> = {
  CLEAN: 'success',
  PENDING: 'secondary',
  INFECTED: 'danger',
  FAILED: 'warning',
};

function humanSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

/**
 * Documents section for the case detail page: upload + list + download.
 * `refreshKey` is bumped by the parent's background poll to re-fetch quietly.
 */
export function CaseDocuments({
  caseId,
  refreshKey,
  onCount,
}: {
  caseId: string;
  refreshKey?: number;
  /** Report the current item count so the parent can badge this tab when it grows unseen. */
  onCount?: (n: number) => void;
}) {
  const [items, setItems] = useState<Document[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [docType, setDocType] = useState<DocumentType>('MEDICAL_REPORT');
  // Id of the document whose presigned URL is currently being fetched, so its View button shows a
  // spinner and disables (the request is a Lambda round-trip that takes ~a second).
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  // Quiet re-fetch (no spinner) — used on user refresh and each poll tick.
  const refresh = () => {
    listCaseDocuments(caseId)
      .then((r) => {
        setItems(r.items);
        onCount?.(r.items.length);
      })
      .catch(() => setError('Failed to load documents'))
      .finally(() => setLoading(false));
  };

  useEffect(refresh, [caseId, refreshKey]);

  const onUpload = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file) return;
    if (file.size > MAX_BYTES) {
      setError('File exceeds the 50 MB limit.');
      return;
    }
    setUploading(true);
    setError(null);
    try {
      await uploadCaseDocument(caseId, file, docType);
      if (fileRef.current) fileRef.current.value = '';
      refresh();
    } catch {
      setError('Upload failed. Please check the file type (PDF, JPEG, PNG) and try again.');
    } finally {
      setUploading(false);
    }
  };

  const onDownload = async (documentId: string) => {
    setDownloadingId(documentId);
    setError(null);
    try {
      const { downloadUrl } = await getCaseDocumentDownloadUrl(documentId);
      window.open(downloadUrl, '_blank', 'noopener');
    } catch (e) {
      const code = (e as { code?: string })?.code;
      setError(
        code === 'DOCUMENT_NOT_AVAILABLE'
          ? 'This document is still being scanned and is not yet available to view.'
          : 'Could not open the document.',
      );
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <Card className="shadow-sm mb-3">
      <Card.Header>Documents</Card.Header>
      <Card.Body>
        {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}

        <Row className="g-2 align-items-end mb-3">
          <Col sm={4}>
            <Form.Label className="small text-muted">Document type</Form.Label>
            <Form.Select value={docType} onChange={(e) => setDocType(e.target.value as DocumentType)}>
              {DOCUMENT_TYPES.map((t) => (
                <option key={t} value={t}>{enumLabel('documentType', t, getLang())}</option>
              ))}
            </Form.Select>
          </Col>
          <Col sm={5}>
            <Form.Label className="small text-muted">File (PDF, JPEG, PNG · max 50 MB)</Form.Label>
            <Form.Control ref={fileRef} type="file" accept={ACCEPT} disabled={uploading} />
          </Col>
          <Col sm={3}>
            <BusyButton className="w-100" onClick={onUpload} loading={uploading} busyLabel="Uploading…">
              Upload
            </BusyButton>
          </Col>
        </Row>

        {loading ? (
          <div className="text-muted d-flex align-items-center">
            <Spinner animation="border" size="sm" className="me-2" /> Loading documents…
          </div>
        ) : items.length === 0 ? (
          <p className="text-muted mb-0">No documents uploaded yet.</p>
        ) : (
          <Table hover responsive className="align-middle mb-0">
            <thead>
              <tr>
                <th>File</th>
                <th>Type</th>
                <th>Size</th>
                <th>Uploaded by</th>
                <th>Scan</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {items.map((d) => (
                <tr key={d.documentId}>
                  <td>{d.fileName}</td>
                  <td className="text-muted small">{enumLabel('documentType', d.documentType, getLang())}</td>
                  <td className="text-muted small">{humanSize(d.fileSize)}</td>
                  <td className="text-muted small">{personName(d.uploadedByName, d.uploadedBy)}</td>
                  <td>
                    <Badge bg={scanVariant[d.scanStatus] ?? 'secondary'}>{d.scanStatus}</Badge>
                  </td>
                  <td className="text-end">
                    <BusyButton
                      variant="outline-primary"
                      size="sm"
                      loading={downloadingId === d.documentId}
                      busyLabel="Opening…"
                      disabled={d.scanStatus !== 'CLEAN'}
                      title={d.scanStatus !== 'CLEAN' ? 'Available once scanning passes' : 'View'}
                      onClick={() => onDownload(d.documentId)}
                    >
                      View
                    </BusyButton>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card.Body>
    </Card>
  );
}
